import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

import { updateSearchParams } from '../../helpers/urlHelpers';

import Pagination from '../../../sharedComponents/Pagination/Pagination';
import PortfolioFolders from './PortfolioFolders';

import actions from '../../actions/FavoritesActions';
import get from 'lodash.get';

class FoldersRefetchWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.onPageChange = this.onPageChange.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        window.onpopstate = this.handlePopState;
        const totalResults = get(this.props, 'viewer.foldersPortfolios.totalResults');
        if (totalResults >= 0) {
            this.props.setTotalResults(totalResults);
        }
    }

    componentDidUpdate(prevProps) {
        const prevTotalResults = get(prevProps, 'viewer.foldersPortfolios.totalResults');
        const totalResults = get(this.props, 'viewer.foldersPortfolios.totalResults');
        const isPageChange = this.props.page !== prevProps.page;

        if (prevTotalResults !== totalResults) {
            this.props.setTotalResults(totalResults);
        }

        if (isPageChange) {
            this.props.setFullPageLoading(true);
            const refetchVariables = fragmentVariables => ({
                ...fragmentVariables,
                page: this.props.page,
            });

            this.props.relay.refetch(
                refetchVariables,
                null,
                () => {
                    this.props.setFullPageLoading(false);
                    window.scrollTo(0, 0);
                },
                { force: true }
            );
        }
    }

    componentWillUnmount() {
        window.onpopstate = null;
    }

    onPageChange = (event, nextPage) => {
        event.preventDefault();
        this.props.resetUiState({ retainItems: true, retainSort: true });
        this.props.setPage(nextPage);
        updateSearchParams('page', nextPage);
        window.scrollTo(0, 0);
    };

    handlePopState = () => {
        if (window.location.search) {
            const params = new URLSearchParams(window.location.search);

            if (params.get('page')) {
                this.props.setPage(+params.get('page'));
            }

            if (params.get('sort')) {
                this.props.setSortBy(params.get('sort'));
            }
        } else {
            this.props.setPage(1);
        }
    };

    render() {
        const { selectedItems, viewer, isMobile } = this.props;
        const totalResults = get(viewer, 'foldersPortfolios.totalResults');
        const numberOfPages = get(this.props, 'viewer.foldersPortfolios.numberOfPages');

        if (!totalResults) {
            return null;
        }

        return (
            <Fragment>
                <PortfolioFolders
                    onDrop={this.props.onDrop}
                    selectedItems={selectedItems}
                    portfoliosConnection={viewer.foldersPortfolios || null}
                    reordered={this.props.reordered}
                />
                <Pagination
                    page={this.props.page}
                    numberOfPages={numberOfPages}
                    onPageChange={this.onPageChange}
                    maxLength={isMobile ? 6 : 9}
                />
            </Fragment>
        );
    }
}

FoldersRefetchWrapper.propTypes = {
    first: PropTypes.number,
    isLoadingPartialPage: PropTypes.bool,
    onDrop: PropTypes.func,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    selectedItems: PropTypes.array,
    setTotalResults: PropTypes.func,
    sortBy: PropTypes.string,
    viewer: PropTypes.object,
    page: PropTypes.number,
    isMobile: PropTypes.bool,
    setPage: PropTypes.func,
    resetUiState: PropTypes.func,
    setFullPageLoading: PropTypes.func,
    setSortBy: PropTypes.func,
};

const mapStateToProps = state => {
    return {
        totalResults: state.favorites.uiState.totalResults,
        page: state.favorites.uiState.page,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setTotalResults: totalResults => {
            dispatch(actions.setTotalResults({ totalResults }));
        },
        setPage: page => {
            dispatch(actions.setPage({ page }));
        },
        resetUiState: ({ retainItems = false, retainSort = false, retainSearch = false } = {}) => {
            dispatch(actions.resetUiState({ retainItems, retainSort, retainSearch }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setSortBy: sortBySelection => {
            dispatch(actions.setSortBy({ sortBySelection }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FoldersRefetchWrapper);

FoldersRefetchWrapper = createRefetchContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment FoldersRefetchWrapper_viewer on Viewer {
                foldersPortfolios: portfolios(
                    first: $first
                    after: $after
                    portfolioTypes: $portfolioTypes
                    sortBy: $foldersSort
                    userIds: $userIds
                    page: $page
                )
                    @connection(key: "FoldersRefetchWrapper_foldersPortfolios")
                    @include(if: $hasUserId) {
                    totalResults
                    numberOfPages
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    edges {
                        cursor
                    }
                    ...PortfolioFolders_portfoliosConnection
                }
            }
        `,
    },
    graphql`
        query FoldersRefetchWrapperRefetchQuery(
            $after: String
            $first: Int!
            $foldersSort: PortfoliosSortByTypes
            $portfolioTypes: [PortfolioTypes]
            $userIds: [String]!
            $hasUserId: Boolean!
            $page: Int!
        ) {
            viewer {
                ...FoldersRefetchWrapper_viewer
            }
        }
    `
);

export default FoldersRefetchWrapper;
